import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { string, object } from 'yup';
import { uniqBy } from 'lodash';
import { useFormik } from 'formik';

// Store
import { Dispatch, RootState } from '../../store/store';

// Constants
import { TEXT_LENGTH_LIMITS } from '../../constants/common';

// Components
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import InputControl from '../../components/InputControl/InputControl';
import SelectGroupOne, { IOptions } from '../../components/Forms/SelectGroup/SelectGroupOne';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import { Contract } from '../../store/models/contracts';
import ROUTE from '../../constants/route-constants';
import { TAB_NUMBER } from '../ContractScreensView/constants';

type CreateScreenProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

type FormValues = {
  name: string | null;
  contractId: string | null;
};

const CreateScreen: React.FC<CreateScreenProps> = ({
  createScreen,
  getContracts,
  contracts,
  getContractById,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { contractId, clientId } = useParams();

  const data: { contractId?: string } | null = location.state;
  const [options, setOptions] = useState<IOptions[]>([
    { value: '', label: 'No client selected', disabled: false },
  ]);
  const [isLoading, setIsLoading] = useState(true);

  const formik = useFormik<FormValues>({
    initialValues: {
      name: '',
      contractId: (data?.contractId || contractId) ?? '',
    },
    validationSchema: object({
      name: string()
        .required('Required')
        .min(TEXT_LENGTH_LIMITS.MIN, `Min first name length is ${TEXT_LENGTH_LIMITS.MIN} characters`)
        .max(TEXT_LENGTH_LIMITS.MAX, `Max first name length is ${TEXT_LENGTH_LIMITS.MAX} characters`),
      contractId: string().required('Required')
    }),
    onSubmit: async (values) => {
      const selectedContract: Contract = contracts.find((item) => item.id === values.contractId);

      await createScreen(values);

      if (contractId) {
        const path = generatePath(ROUTE.LOCATION_CONTRACT_SCREENS, { clientId: selectedContract.locationId, contractId: selectedContract.id });
        navigate(path);
      }

      if (clientId) {
        const path = generatePath(ROUTE.LOCATION_CONTRACTS_OVERVIEW, { clientId });
        navigate(path, { state: { tabNumber: TAB_NUMBER.ACTIVE } });
      }
    },
  });

  const isDisabledBtn = Boolean((formik.touched?.name && formik.errors?.name) || (formik.touched?.contractId && formik.errors?.contractId));

  const fetchData = async () => {
    await getContracts();
    if (data?.contractId || contractId) {
      await getContractById(data?.contractId || contractId);
    }
    setIsLoading(false);
  };

  const selectOption = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;

    formik.handleChange({
      target: { name, value },
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const options: IOptions[] = contracts.map((contract) => {
      return ({
        value: contract.id,
        label: contract.name,
        disabled: false,
      });
    });

    setOptions((prev) => uniqBy([...prev, ...options], 'value'));
  }, [contracts]);

  return (
    <>
      {isLoading && (
        <div className="h-full w-full flex items-center justify-center">
          <SpinnerLoader />
        </div>
      )}
      {!isLoading && (
        <div className="p-create-screen pb-4 md:pb-25">
          <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
              <h3 className="font-medium text-black dark:text-white">
                Create Screen
              </h3>
            </div>
            <form className="flex w-full flex-col gap-5.5 p-7" onSubmit={formik.handleSubmit} >
              <InputControl
                label="Name"
                type="text"
                name="name"
                placeholder="Enter your screen's name"
                value={formik.values.name}
                customOnChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showError={formik.touched.name && formik.errors.name}
                errorText={formik.errors.name}
              />
              <SelectGroupOne
                label="Contract"
                handleChange={selectOption}
                options={options}
                selectedOption={formik.values.contractId ?? ''}
                name="contractId"
              />
              <div className="flex justify-end gap-4.5">
                <InteractiveButton
                  text="Create"
                  submit
                  disabled={isDisabledBtn}
                  isLoading={formik.isSubmitting}
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  contracts: state.contracts.contracts,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getContracts: dispatch.contracts.getContracts,
  getContractById: dispatch.contracts.getContract,
  createScreen: dispatch.screens.create,
});

export default connect(mapState, mapDispatch)(CreateScreen);
